import type { CSSProperties } from 'react';
import { normalizeButtonDesignStyle } from '../../../lib/buttonDesignStyles';

export type ImageFitMode = 'cover' | 'contain' | 'fill';

export type ImageTransformStyle = {
  imageFit: ImageFitMode;
  imageScale: number;
  imageScaleX: number;
  imageScaleY: number;
  imageRotation: number;
  imagePositionX: number;
  imagePositionY: number;
  imageFlipX: boolean;
  imageFlipY: boolean;
};

export type ImageTransformPatch = Partial<ImageTransformStyle>;

export type ImageAdjustInteractionKind =
  | 'move'
  | 'scale-uniform'
  | 'scale-x'
  | 'scale-y'
  | 'rotate';

export type ImageTransformHandleKind =
  | 'corner-nw'
  | 'corner-ne'
  | 'corner-se'
  | 'corner-sw'
  | 'side-n'
  | 'side-e'
  | 'side-s'
  | 'side-w'
  | 'rotate-nw'
  | 'rotate-ne'
  | 'rotate-se'
  | 'rotate-sw';

export const IMAGE_TRANSFORM_DEFAULTS: ImageTransformStyle = {
  imageFit: 'cover',
  imageScale: 100,
  imageScaleX: 1,
  imageScaleY: 1,
  imageRotation: 0,
  imagePositionX: 0,
  imagePositionY: 0,
  imageFlipX: false,
  imageFlipY: false,
};

export const IMAGE_FIT_OPTIONS: Array<{
  value: ImageFitMode;
  label: string;
  description: string;
}> = [
  { value: 'cover', label: 'Cover frame', description: 'Fill the frame; cropping is expected if ratios differ.' },
  { value: 'contain', label: 'Contain full image', description: 'Show the full image inside the frame.' },
  { value: 'fill', label: 'Stretch to frame', description: 'Stretch exactly to the frame; distortion is expected.' },
];

export function clampImagePositionPercent(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(-100, Math.min(100, Number(value.toFixed(2))));
}

export function clampImageZoomPercent(value: number) {
  if (!Number.isFinite(value)) return IMAGE_TRANSFORM_DEFAULTS.imageScale;
  return Math.max(10, Math.min(300, Number(value.toFixed(2))));
}

export function clampImageAxisScale(value: number) {
  if (!Number.isFinite(value)) return 1;
  return Math.max(0.1, Math.min(5, Number(value.toFixed(3))));
}

export function normalizeImageRotation(value: number) {
  if (!Number.isFinite(value)) return 0;
  let next = Number(value.toFixed(2)) % 360;
  if (next > 180) next -= 360;
  if (next <= -180) next += 360;
  return next;
}

export function normalizeImageTransformStyle(
  styleValue: Record<string, unknown> | undefined
): ImageTransformStyle {
  const normalizedStyle = normalizeButtonDesignStyle(styleValue);
  return {
    imageFit: normalizedStyle.imageFit ?? IMAGE_TRANSFORM_DEFAULTS.imageFit,
    imageScale: normalizedStyle.imageScale ?? IMAGE_TRANSFORM_DEFAULTS.imageScale,
    imageScaleX: normalizedStyle.imageScaleX ?? IMAGE_TRANSFORM_DEFAULTS.imageScaleX,
    imageScaleY: normalizedStyle.imageScaleY ?? IMAGE_TRANSFORM_DEFAULTS.imageScaleY,
    imageRotation: normalizedStyle.imageRotation ?? IMAGE_TRANSFORM_DEFAULTS.imageRotation,
    imagePositionX: normalizedStyle.imagePositionX ?? IMAGE_TRANSFORM_DEFAULTS.imagePositionX,
    imagePositionY: normalizedStyle.imagePositionY ?? IMAGE_TRANSFORM_DEFAULTS.imagePositionY,
    imageFlipX: normalizedStyle.imageFlipX ?? IMAGE_TRANSFORM_DEFAULTS.imageFlipX,
    imageFlipY: normalizedStyle.imageFlipY ?? IMAGE_TRANSFORM_DEFAULTS.imageFlipY,
  };
}

export function buildImageTransformStyle(
  styleValue: Record<string, unknown> | undefined
): CSSProperties {
  const normalized = normalizeImageTransformStyle(styleValue);
  const scaleMultiplier = normalized.imageScale / 100;
  const scaleX =
    scaleMultiplier *
    normalized.imageScaleX *
    (normalized.imageFlipX ? -1 : 1);
  const scaleY =
    scaleMultiplier *
    normalized.imageScaleY *
    (normalized.imageFlipY ? -1 : 1);

  return {
    objectFit: normalized.imageFit,
    objectPosition: '50% 50%',
    transform: `translate(${normalized.imagePositionX}%, ${normalized.imagePositionY}%) rotate(${normalized.imageRotation}deg) scale(${scaleX}, ${scaleY})`,
    transformOrigin: 'center center',
    willChange: 'transform',
  };
}

export function buildImageTransformPatch(
  currentStyle: Record<string, unknown> | undefined,
  patch: ImageTransformPatch
): Record<string, unknown> {
  return {
    ...(currentStyle ?? {}),
    ...normalizeImageTransformStyle(currentStyle),
    ...patch,
  };
}
